import { DateTime } from 'luxon';

import { type ApolloClient } from '@apollo/client';

import {
  AddressFragment,
  DraftDocument,
  DraftFragment,
  DraftInput,
  DraftSaveAndNotifyDocument,
  DraftSaveAndNotifyMutation,
  DraftSaveAndNotifyMutationVariables,
  DraftUpsertDocument,
  DraftUpsertMutation,
  DraftUpsertMutationVariables,
  Draft__Address,
  Draft__MovingAddress,
  Lead__ServiceNeeds,
  PricingSetFragment,
  RateGroupKindEnum,
  Status,
} from '@graphql/platform';

import { RATE_GROUP_COMMITMENT } from '@root/constants/pricing';
import { WT_VISITOR_TOKEN } from '@root/initializers/wt';
import { PlanName } from '@root/resources/types/plan_name';

import { geocode } from '@utils/geocode';
import { extractPlan, extractRateGroup } from '@utils/pricing/pricing_set';
import { ProtectionPlanEnum } from '@utils/protection_plan';

import { StorageCheckoutData } from '../data';
import { CheckoutVersion } from '../types';
import { InitialValues } from './initial_values/types';
import stringify from 'fast-json-stable-stringify';

let lastStorageDraft: string | undefined;

function resolveDatePreferred(dateString?: string) {
  if (!dateString) return undefined;
  const parsed = DateTime.fromISO(dateString);
  return parsed.toMillis() > Date.now() ? parsed : undefined;
}

function convertAddress(
  address: Draft__Address | Draft__MovingAddress,
): Partial<AddressFragment> {
  return {
    street: address.street ?? undefined,
    aptsuite: address.aptsuite ?? undefined,
    zip: address.zip ?? undefined,
    city: address.city ?? undefined,
    state: address.state ?? undefined,
  };
}

function extractStorageData(draft: DraftFragment) {
  const data: Partial<InitialValues['storageData']> = {};

  if (draft.address) {
    data.address = convertAddress(draft.address);
  }

  if (draft.rateGroup) {
    data.commitment = RATE_GROUP_COMMITMENT[draft.rateGroup.name];
  } else {
    data.commitment = RateGroupKindEnum.Flexer;
  }

  if (draft.plan?.name) {
    data.planSize = draft.plan.name as PlanName;
  }

  if (draft.protectionPlanSlug) {
    data.protectionPlan = draft.protectionPlanSlug as ProtectionPlanEnum;
  }

  return data;
}

const buildStorageDraftValues = (
  data: StorageCheckoutData,
  pricingSet: PricingSetFragment,
) => {
  const { zip, address, planSize, commitment, dateScheduled, protectionPlan } =
    data;

  const draftValues: Partial<DeepMutable<DraftInput>> = {};

  if (zip) {
    draftValues.address = {
      zip: zip,
      street: address?.street,
      city: address?.city,
      state: address?.state,
    };
  }

  if (planSize && commitment && pricingSet) {
    draftValues.storagePlanID = extractPlan(
      commitment,
      planSize,
      pricingSet,
    )?.id;
  }

  if (commitment) {
    draftValues.rateGroupID = extractRateGroup(commitment, pricingSet)?.id;
  }

  if (dateScheduled) {
    draftValues.preferredTime = dateScheduled.fromTime;
  }

  if (protectionPlan) {
    draftValues.protectionPlanSlug = protectionPlan;
  }

  if (Object.keys(draftValues).length !== 0) {
    draftValues.latestService = Lead__ServiceNeeds.SmartStorage;
    draftValues.funnelVersion = CheckoutVersion.Storage;
    return draftValues;
  }
};

const upsertDraft = (client: ApolloClient, input: DraftInput) => {
  client
    .mutate<DraftUpsertMutation, DraftUpsertMutationVariables>({
      mutation: DraftUpsertDocument,
      variables: {
        input,
      },
    })
    .then((result) => {
      if (result.data?.draftUpsert?.draft) {
        client.writeQuery({
          data: { draft: result.data?.draftUpsert?.draft },
          query: DraftDocument,
        });
      }
    });
};

export const updateStorageDraft = (
  client: ApolloClient,
  data: StorageCheckoutData,
  leadToken?: string,
  pricingSet?: PricingSetFragment,
) => {
  if (!pricingSet) return;

  const values = buildStorageDraftValues(data, pricingSet);
  const stringified = values && stringify(values);

  // updateDraft may be called when non-draft values have changed, so we guard
  // against noop API calls
  if (values && stringified !== lastStorageDraft) {
    lastStorageDraft = stringified;
    upsertDraft(client, {
      leadToken,
      visitorToken: WT_VISITOR_TOKEN,
      quoteID: pricingSet.quoteId,
      ...values,
    });
  }
};

/**
 * Persist the current storage checkout draft, attach the lead, and email a
 * resume link (DraftSaveAndNotify / QUOTE_SAVED).
 */
export const saveStorageDraftAndNotify = async (
  client: ApolloClient,
  data: StorageCheckoutData,
  leadToken: string,
  pricingSet: PricingSetFragment,
): Promise<{ ok: true } | { ok: false; error: string }> => {
  const values = buildStorageDraftValues(data, pricingSet) ?? {
    latestService: Lead__ServiceNeeds.SmartStorage,
    funnelVersion: CheckoutVersion.Storage,
  };

  const upsertResult = await client.mutate<
    DraftUpsertMutation,
    DraftUpsertMutationVariables
  >({
    mutation: DraftUpsertDocument,
    variables: {
      input: {
        leadToken,
        visitorToken: WT_VISITOR_TOKEN,
        quoteID: pricingSet.quoteId,
        ...values,
      },
    },
  });

  const draft = upsertResult.data?.draftUpsert?.draft;
  if (!draft?.id) {
    return {
      ok: false,
      error: 'Unable to save your progress. Please try again.',
    };
  }

  lastStorageDraft = stringify(values);
  client.writeQuery({
    data: { draft },
    query: DraftDocument,
  });

  const notifyResult = await client.mutate<
    DraftSaveAndNotifyMutation,
    DraftSaveAndNotifyMutationVariables
  >({
    mutation: DraftSaveAndNotifyDocument,
    variables: { id: draft.id },
  });

  const payload = notifyResult.data?.draftSaveAndNotify;
  if (payload?.status !== Status.Ok) {
    return {
      ok: false,
      error:
        payload?.error ||
        'Unable to email your saved progress. Please try again.',
    };
  }

  return { ok: true };
};

export const parseDraftToValues = async (
  client: ApolloClient,
  draft: DraftFragment,
) => {
  const storageZip = draft?.address?.zip ?? undefined;

  const sharedProperties = {
    datePreferred: resolveDatePreferred(draft?.preferredTime ?? undefined),
    leadToken: draft?.lead?.token,
    email: draft?.lead?.email ?? undefined,
    name: draft.lead?.name ?? undefined,
    phone: draft.lead?.phone ?? undefined,
  };

  const geocodeResult = storageZip
    ? (await geocode(client, storageZip))?.geocode
    : undefined;

  const storageData: Omit<InitialValues['storageData'], 'checkoutType'> = {
    ...sharedProperties,
    ...(draft ? extractStorageData(draft) : {}),
    zip: storageZip,
    zipValidated: geocodeResult?.eligibleForDoorToDoor,
  };

  return {
    storageData,
  };
};
